<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted } from 'vue';
import { UiCard } from '@shopexperts/shared';
import { useExpertReviews } from '../../composables/reviews/useExpertReviews';
import ExpertReviewCard from '../../components/reviews/ExpertReviewCard.vue';
import RespondToReviewModal from '../../components/reviews/RespondToReviewModal.vue';
import ShareReviewDialog from '../../components/reviews/ShareReviewDialog.vue';
import EmptyDataPlaceholder from '@/components/common/EmptyDataPlaceholder.vue';
import { useProfileStatus } from '@/pages/expert/listing/composables/useProfileStatus';
import { getReviewReplyAccess } from '@/app/utils/reviewReplyAccess';

const { reviews, fetchNextPage, hasNextPage, isFetchingNextPage, isLoading } = useExpertReviews();
const { isPro } = useProfileStatus();
const replyAccess = computed(() => getReviewReplyAccess(isPro.value));

const sentinelRef = ref<HTMLElement | null>(null);
const showRespondModal = ref(false);
const selectedReviewId = ref<number | null>(null);
const selectedReviewResponse = ref<string | undefined>(undefined);
const shareReview = ref<(typeof reviews.value)[number] | null>(null);
const showShareDialog = ref(false);

function handleShare(reviewId: number) {
  shareReview.value = reviews.value.find(review => review.id === reviewId) ?? null;
  showShareDialog.value = !!shareReview.value;
}

// Intersection observer for infinite scroll
let observer: IntersectionObserver | null = null;

onMounted(() => {
  observer = new IntersectionObserver(
    (entries) => {
      if (entries[0]?.isIntersecting && hasNextPage.value && !isFetchingNextPage.value) {
        fetchNextPage();
      }
    },
    { threshold: 0.1 }
  );

  if (sentinelRef.value) {
    observer.observe(sentinelRef.value);
  }
});

onUnmounted(() => {
  if (observer) {
    observer.disconnect();
  }
});

const handleRespond = (reviewId: number) => {
  const review = reviews.value.find(r => r.id === reviewId);
  selectedReviewId.value = reviewId;
  selectedReviewResponse.value = review?.review_response?.response_text;
  showRespondModal.value = true;
};
</script>

<template>
  <div class="space-y-4">
    <!-- Loading State -->
    <div v-if="isLoading" class="space-y-4">
      <UiCard
        v-for="i in 3"
        :key="i"
        padding="md"
        class="animate-pulse"
      >
        <div class="flex items-start gap-4">
          <div class="w-12 h-12 bg-gray-200 rounded-full"></div>
          <div class="flex-1 space-y-3">
            <div class="h-4 bg-gray-200 rounded w-1/4"></div>
            <div class="h-4 bg-gray-200 rounded w-3/4"></div>
            <div class="h-4 bg-gray-200 rounded w-1/2"></div>
          </div>
        </div>
      </UiCard>
    </div>

    <!-- Empty State -->
    <EmptyDataPlaceholder
      v-else-if="!isLoading && reviews.length === 0"
      :is-reviews-page="true"
      title="No Reviews Yet"
      description="You haven't received any reviews yet. Start requesting reviews from your clients to build your reputation."
    />

    <!-- Reviews List -->
    <template v-else>
      <ExpertReviewCard
        v-for="review in reviews"
        :key="review.id"
        :review="review"
        :can-reply="replyAccess.canReply"
        :reply-locked-tooltip="replyAccess.tooltip"
        @respond="handleRespond"
        @share="handleShare"
      />

      <!-- Infinite Scroll Sentinel -->
          <div
            ref="sentinelRef"
            class="h-10 flex items-center justify-center"
          >
            <div v-if="isFetchingNextPage" class="animate-spin rounded-full h-6 w-6 border-b-2 border-gray-900"></div>
          </div>
    </template>

    <!-- Respond Modal -->
    <ShareReviewDialog v-if="showShareDialog && shareReview" :key="shareReview.id"
      v-model:open="showShareDialog" :review="shareReview" />
    <RespondToReviewModal
      v-if="selectedReviewId"
      v-model:open="showRespondModal"
      :review-id="selectedReviewId"
      :existing-response="selectedReviewResponse"
    />
  </div>
</template>
